import { CollectionResource } from 'dy-frontend-http-repository/lib/data/resources';
import {
    CreateTaskSubTicketInput,
    ReorderTaskSubTicketsInput,
    TaskSubTicketDesignerPickerFilterInput,
    TaskSubTicketStatusLogFilterInput,
    TaskSubTicketSummaryFilterInput,
    TaskSubTicketsFilterInput,
    UpdateTaskSubTicketAssigneeInput,
    UpdateTaskSubTicketInput,
    UpdateTaskSubTicketStatusInput,
} from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/inputs';
import { TaskSubTicketRef } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/refs';
import {
    DesignerPickerItemResource,
    TaskSubTicketDetailResource,
    TaskSubTicketOrderResource,
    TaskSubTicketResource,
    TaskSubTicketStatusLogResource,
    TaskSubTicketSummaryResource,
} from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';
import { StopTimeTrackingSessionInput } from 'dy-frontend-http-repository/lib/modules/TimeTacking/inputs';
import {
    TimeTrackingSessionHeartbeatResource,
    TimeTrackingSessionResource,
    TimeTrackingSessionStopResource,
} from 'dy-frontend-http-repository/lib/modules/TimeTacking/resources';
import repository from 'dy-frontend-http-repository/lib/repository';

import domain from './domain';
import { GenerationScoped } from './requestGeneration';

// Fetch sub-tickets of the parent Request
export const fetchSubTickets = domain.createEffect<
    { filter: TaskSubTicketsFilterInput } & GenerationScoped,
    CollectionResource<TaskSubTicketResource, {}>
>({
    name: 'fetchSubTickets',
    handler: async ({ filter }) => {
        return await repository.taskSubTicket().get(filter);
    },
});

// Fetch aside summary counters
export const fetchSubTicketSummary = domain.createEffect<
    { filter: TaskSubTicketSummaryFilterInput } & GenerationScoped,
    TaskSubTicketSummaryResource
>({
    name: 'fetchSubTicketSummary',
    handler: async ({ filter }) => {
        return await repository.taskSubTicket().getSummary(filter);
    },
});

// Fetch the parent Request's sub-task status transitions, for its own state log. Status rows
// only: anything else rendered there would read as a transition that never happened
export const fetchSubTicketStatusLog = domain.createEffect<
    { filter: TaskSubTicketStatusLogFilterInput },
    CollectionResource<TaskSubTicketStatusLogResource, {}>
>({
    name: 'fetchSubTicketStatusLog',
    handler: async ({ filter }) => {
        return await repository.taskSubTicket().getStatusLogs(filter);
    },
});

// Set the display order of the parent Request's non-archived sub-tickets. Writes display
// position only - the immutable ordinal labels never move - and returns the committed order
export const reorderSubTicketList = domain.createEffect<ReorderTaskSubTicketsInput, TaskSubTicketOrderResource>({
    name: 'reorderSubTicketList',
    handler: async (input) => {
        return await repository.taskSubTicket().reorder(input);
    },
});

// Fetch assignable designers together with their parent participation flag
export const fetchDesignerPickerItems = domain.createEffect<
    { filter: TaskSubTicketDesignerPickerFilterInput } & GenerationScoped,
    CollectionResource<DesignerPickerItemResource, {}>
>({
    name: 'fetchDesignerPickerItems',
    handler: async ({ filter }) => {
        return await repository.taskSubTicket().getDesignerPicker(filter);
    },
});

// Create sub-ticket
export const createSubTicket = domain.createEffect<CreateTaskSubTicketInput, TaskSubTicketRef>({
    name: 'createSubTicket',
    handler: async (input) => {
        return await repository.taskSubTicket().create(input);
    },
});

// Update sub-ticket scope, expected deliverables and due date
export const updateSubTicket = domain.createEffect<{ id: ID; input: UpdateTaskSubTicketInput }, TaskSubTicketRef>({
    name: 'updateSubTicket',
    handler: async ({ id, input }) => {
        return await repository.taskSubTicket().update(id, input);
    },
});

// Change sub-ticket status. Never happens automatically - not on timer start, not on stop
export const updateSubTicketStatus = domain.createEffect<
    { id: ID; input: UpdateTaskSubTicketStatusInput },
    TaskSubTicketRef
>({
    name: 'updateSubTicketStatus',
    handler: async ({ id, input }) => {
        return await repository.taskSubTicket().updateStatus(id, input);
    },
});

// Reassign the sub-ticket designer. Rejected with 409 while a timer runs on the sub-ticket;
// the previous designer keeps their parent participation and their logged history
export const updateSubTicketAssignee = domain.createEffect<
    { id: ID; input: UpdateTaskSubTicketAssigneeInput },
    TaskSubTicketRef
>({
    name: 'updateSubTicketAssignee',
    handler: async ({ id, input }) => {
        return await repository.taskSubTicket().updateAssignee(id, input);
    },
});

// Archive sub-ticket
export const archiveSubTicket = domain.createEffect<ID, TaskSubTicketRef>({
    name: 'archiveSubTicket',
    handler: async (id) => {
        return await repository.taskSubTicket().archive(id);
    },
});

// Restore archived sub-ticket
export const restoreSubTicket = domain.createEffect<ID, TaskSubTicketRef>({
    name: 'restoreSubTicket',
    handler: async (id) => {
        return await repository.taskSubTicket().restore(id);
    },
});

// Get the caller's open session, or null. This is what restores the timer after a refresh
export const fetchActiveTimerSession = domain.createEffect<
    GenerationScoped,
    TimeTrackingSessionResource | null
>({
    name: 'fetchActiveTimerSession',
    handler: async () => {
        return await repository.timeTracking().getActiveSession();
    },
});

// Single sub-ticket with its audit log and timer session history.
//
// `taskId` is carried so the generation key can be scoped to the identity being fetched: two
// details are legitimately in flight at once (the expanded panel and the label for a timer
// running on another phase) and neither may settle the other.
export const fetchSubTicketDetail = domain.createEffect<
    { taskId: ID; id: ID } & GenerationScoped,
    TaskSubTicketDetailResource
>({
    name: 'fetchSubTicketDetail',
    handler: async ({ id }) => {
        return await repository.taskSubTicket().getById(id);
    },
});

// Start a timer on a sub-ticket. Starting while another session is open is refused with 409
export const startTimerSession = domain.createEffect<ID, TimeTrackingSessionResource>({
    name: 'startTimerSession',
    handler: async (taskSubTicketId) => {
        return await repository.timeTracking().startSession({ task_sub_ticket_id: taskSubTicketId });
    },
});

// Keep the open session alive; posted every 60s while the workspace tab is open
export const heartbeatTimerSession = domain.createEffect<ID, TimeTrackingSessionHeartbeatResource>({
    name: 'heartbeatTimerSession',
    handler: async (id) => {
        return await repository.timeTracking().heartbeatSession(id);
    },
});

// Stop & Save. Idempotent: a repeated stop returns the same entry id and writes nothing
export const stopTimerSession = domain.createEffect<
    { id: ID; input: StopTimeTrackingSessionInput },
    TimeTrackingSessionStopResource
>({
    name: 'stopTimerSession',
    handler: async ({ id, input }) => {
        return await repository.timeTracking().stopSession(id, input);
    },
});
